/**
 * [PM-SG DEBRIEF 2026-09-18, user] PIT DEBRIEF — what everyone behind the podium has to do to
 * reach P1.
 *
 * Replaces ROLL OF HONOUR. In Formula One the debrief is the post-session sit-down where the
 * engineer tells the driver exactly what to fix, which is precisely the page the user asked for:
 * *"it will show how the rank four to the last should perform to get to the rank one… if it is
 * missing with upsell then it will show a line which will say you have to work on your upsell"*.
 *
 * [PM-SG DEBRIEF ONE-CARD 2026-09-21, user] ONE CARD AT A TIME. Three cards side by side was
 * *"too much theory part to be readable easily"*. A visit still covers three names, but they are
 * shown one after another: the card takes the left ~80% of the page for its share of the dwell
 * (30s each at 90s), and a stack of name plates down the right ~20% says who is next, so the room
 * is ready for the next face before it arrives.
 *
 *   THE HANDOFF (user's pick of three researched). The NEXT plate's countdown bar runs out;
 *   the card on screen slides away to the left while the next one comes out of the stack, leaning in and straightening as it brakes; its lines land top to bottom; the plates
 *   move up a slot. About 1.3s of motion, then nothing moves under the figures until the next
 *   swap. Transform and opacity only - it has to stay smooth on a 50Hz panel.
 *
 *   ⚠ NO DRIVING CAR. Researched and rejected: a car crossing the page in under a second moves
 *   ~54px a frame at 50Hz and judders, and a second driving car would collapse this page back
 *   into RACE.
 *
 *   THE STACK ALWAYS SHOWS THE NEXT THREE NAMES IN RANK ORDER (user's pick). What is left of this
 *   visit first; anything after that belongs to the next visit and sits under a NEXT VISIT label,
 *   so on the last card the stack still says what comes next instead of going empty.
 *
 * STRICT RANK ORDER. P4-P5-P6 on one visit, P7-P8-P9 on the next, then P10-P11-P12, and once the
 * last name has been covered it starts again from rank 4.
 *
 * ⚠ A SHUFFLED ORDER WAS BUILT AND REMOVED THE SAME DAY (2026-09-18). Randomising put P7, P11
 * and P5 on one page, and positions jumping around read as broken data rather than as variety.
 * Rank order is the page.
 *
 * ⚠ THE VISIT IS OWNED BY PMSingaporePage, NOT BY THIS VIEW. Every view on this board is
 * conditionally rendered, so this component genuinely unmounts when the circuit moves on. The
 * parent picks the group (three RANKS) and remembers which card was up when an announcement took
 * the screen, so the board comes back to that same card. This view runs the clock between the
 * cards of one visit and reports each one back.
 *
 * [2026-09-21, user] WHO IS ON A CARD IS DECIDED WHEN THE CARD COMES UP, not when the visit
 * starts. The card on screen keeps its person until its share is up; every card after it, and
 * every name in the stack, is whoever holds that rank at that moment - so only ranks 4 to last
 * ever appear here.
 *
 * ⚠ A SHORT LAST GROUP GETS LONGER CARDS, IT DOES NOT WRAP. Eleven chasers give a final group of
 * two; topping it up with rank 4 would show rank 4 twice inside one cycle, which contradicts the
 * user's *"till we complete all the names, and then again it will repeat"*. The parent splits the
 * dwell evenly instead - 45s each for two - so the page never sits on an empty slot.
 *
 * WHAT A CARD SAYS is decided by the SERVER (routes/pmSingapore.ts, buildCoaching) — "why is this
 * PM not P1" is business logic, and the same rule has to hold wherever it is shown. This file
 * only chooses the words.
 */
import React, { useEffect, useRef, useState } from 'react';
import styled, { keyframes, css } from 'styled-components';
import { INK, TEXT, RED, AMBER, GREEN, LINE, EASE, FONT, SideCarSvg } from './chrome';
import { LeaderData, CoachCard, fmtNum, fmtPctValue } from './raceModel';

/** Names per visit. PMSingaporePage walks the field in groups of this size. */
export const DEBRIEF_GROUP = 3;

/** One arrival at the debrief, as the parent decided it. Frozen for the whole visit. */
export interface DebriefVisit {
  /** Bumps on every arrival; the view is keyed on it. */
  visit: number;
  /** Which group of DEBRIEF_GROUP this visit shows, and how many the field made at the time. */
  group: number;
  groups: number;
  /** The group's RANK SLOTS, as they stood when the visit began. Who fills each slot is read live
   *  when its card comes up (holderOf); the figures are read live by name. */
  cards: CoachCard[];
  /** The card to open on — 0, or the one an announcement interrupted. */
  start: number;
  perCardMs: number;
}

/* ── motion ─────────────────────────────────────────────────────────────────── */

/**
 * The first card of a visit waits for the page transition to open. The board swaps pages 900ms
 * into its 2000ms sweep (PMSingaporePage SWEEP_COVER_MS), and every sweep starts uncovering at 80%
 * - so the screen begins to open 700ms after this view mounts. Arriving any earlier would spend
 * the entrance under the cover, which is what the old three-card fade did.
 */
const FIRST_DELAY_MS = 700;
/** The old card starts leaving first; the new one follows this far behind it. */
const IN_LAG_MS = 160;
const IN_MS = 680;
const OUT_MS = 380;
/** The lines land after the card has mostly braked, one after another. */
const LINE_LEAD_MS = 240;
const LINE_STEP_MS = 70;
const PLATE_LAG_MS = 220;
const PLATE_STEP_MS = 70;

/* Decelerate in, accelerate out, no overshoot anywhere: F1's own graphics settle, they never
   bounce, and a bounce repeated a hundred times an hour turns into a tic. */
const EASE_IN_CARD = 'cubic-bezier(.16,1,.3,1)';
const EASE_OUT_CARD = 'cubic-bezier(.4,0,1,1)';

/* Positive skew leans the top of the card into its direction of travel, which is leftward here. */
const cardIn = keyframes`
  0%   { opacity: 0; transform: translateX(56%) skewX(8deg); }
  30%  { opacity: 1; }
  100% { opacity: 1; transform: translateX(0) skewX(0deg); }
`;
const cardOut = keyframes`
  to { opacity: 0; transform: translateX(-64%) skewX(8deg); }
`;
const lineIn = keyframes`from { opacity: 0; transform: translateY(14px); }`;
const fadeIn = keyframes`from { opacity: 0; }`;
const fadeOut = keyframes`to { opacity: 0; }`;

const plateIn = keyframes`from { opacity: 0; transform: translateY(30px); }`;
/** The plate that has just been called leaves the stack towards the card it became. */
const plateOut = keyframes`to { opacity: 0; transform: translateX(-60%); }`;
const countdown = keyframes`from { transform: scaleX(0); } to { transform: scaleX(1); }`;

/** A slow highlight raked across the plate — the only thing on this page that loops. */
const rake = keyframes`
  0%        { transform: translateX(-120%) skewX(-14deg); }
  38%, 100% { transform: translateX(320%)  skewX(-14deg); }
`;

/* ── ground ─────────────────────────────────────────────────────────────────── */

/* ⚠ HORIZONTAL SIZES ARE PERCENTAGES, VERTICAL SIZES ARE PX. The canvas is always 1080 tall but
   1600-2600 wide, so a px width would drift across screens while a px height never does. */
const View = styled.div`
  position: absolute;
  inset: 0;
  background: ${INK};
  font-family: ${FONT};
  color: ${TEXT};
  display: flex;
  flex-direction: column;
  padding: 30px 5% 26px;
  overflow: hidden;

  /* z-index 5 so the grain lies over the stack as well as the card - the stack sits at 3 to
     hide the incoming card as it slides out from under it. */
  &::after {
    content: '';
    position: absolute;
    inset: 0;
    z-index: 5;
    opacity: 0.15;
    mix-blend-mode: overlay;
    pointer-events: none;
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='140' height='140'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='.85' numOctaves='3'/%3E%3C/filter%3E%3Crect width='140' height='140' filter='url(%23n)' opacity='.42'/%3E%3C/svg%3E");
  }
`;

const Head = styled.header`
  flex: 0 0 auto;
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 3%;
  padding-bottom: 14px;
  border-bottom: 2px solid ${LINE};

  .mark {
    display: flex;
    align-items: baseline;
    gap: 14px;
  }
  .m1 {
    font-size: 58px;
    font-weight: 900;
    letter-spacing: 0.06em;
  }
  .m2 {
    font-size: 58px;
    font-weight: 900;
    letter-spacing: 0.06em;
    color: ${RED};
  }
  .sub {
    font-size: 21px;
    font-weight: 700;
    letter-spacing: 0.18em;
    color: rgba(255, 255, 255, 0.72);
  }
  .page {
    text-align: right;
    font-size: 21px;
    font-weight: 900;
    letter-spacing: 0.16em;
    color: ${AMBER};
    white-space: nowrap;
  }
`;

/* The card on the left, the stack on the right. Main takes whatever the stack does not, which
   lands it at ~79% of the width. */
const Bay = styled.div`
  flex: 1 1 auto;
  display: flex;
  align-items: stretch;
  gap: 2%;
  padding: 26px 0 18px;
  min-height: 0;
`;

/* Both the leaving and the arriving card live in here, stacked, for the ~0.8s they overlap. */
const Main = styled.div`
  position: relative;
  flex: 1 1 0;
  min-width: 0;
`;

/* The chamfer is PitWallView's slab, so the debrief reads as the same instrument family.
   ⚠ The background is OPAQUE now (the old translucent sheen laid over INK, which is the colour it
   always sat on, so it looks the same). During the swap two cards overlap, and a see-through card
   would print one PM's figures through another's. */
const Card = styled.article<{ $entry: 'first' | 'swap'; $leaving: boolean }>`
  position: absolute;
  inset: 0;
  z-index: ${(p) => (p.$leaving ? 1 : 2)};
  display: flex;
  flex-direction: column;
  min-width: 0;
  padding: 30px 40px 26px 44px;
  background: linear-gradient(160deg, rgba(255, 255, 255, 0.06), rgba(255, 255, 255, 0.015)), ${INK};
  border: 1px solid ${LINE};
  clip-path: polygon(34px 0, 100% 0, 100% calc(100% - 34px), calc(100% - 34px) 100%, 0 100%, 0 34px);
  overflow: hidden;
  ${(p) => (p.$leaving
    ? css`animation: ${cardOut} ${OUT_MS}ms ${EASE_OUT_CARD} forwards;`
    : css`animation: ${cardIn} ${IN_MS}ms ${EASE_IN_CARD} ${p.$entry === 'first' ? FIRST_DELAY_MS : IN_LAG_MS}ms backwards;`)}

  /* Each block lands in reading order. The delay depends only on how the card ARRIVED, never
     on whether it is leaving, so the rule is identical on the way out and the lines do not
     replay while the card slides away. */
  .ln {
    animation: ${lineIn} 440ms ${EASE} backwards;
    animation-delay: calc(${(p) => (p.$entry === 'first' ? FIRST_DELAY_MS : IN_LAG_MS) + LINE_LEAD_MS}ms + var(--i, 0) * ${LINE_STEP_MS}ms);
  }

  /* The PM's own livery, down the leading edge. */
  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 34px;
    bottom: 0;
    width: 8px;
    background: var(--c);
    box-shadow: 0 0 20px var(--c);
  }

  /* [2026-09-18, user] TONED DOWN — it was reading as a flash across the card rather than as
     light moving over bodywork. On a wall board a highlight that catches the eye competes with
     the figures, which are the reason the card exists. */
  .rake {
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    width: 34%;
    background: linear-gradient(
      90deg,
      transparent,
      rgba(255, 255, 255, 0.012) 30%,
      rgba(255, 255, 255, 0.026) 50%,
      rgba(255, 255, 255, 0.012) 70%,
      transparent
    );
    animation: ${rake} 13s ${EASE} infinite;
    pointer-events: none;
  }

  @media (prefers-reduced-motion: reduce) {
    ${(p) => (p.$leaving
    ? css`animation: ${fadeOut} 250ms ease forwards;`
    : css`animation: ${fadeIn} 250ms ease backwards;`)}
    .ln { animation: none; }
  }
`;

/* [2026-09-21] WHO, WHAT THEY HAVE, AND HOW THEY RANK, in one band across the top. One card has
   the whole width now, so the position, the name and the efficiency sit on one line and every
   figure can be set well above the 37px ten-foot floor the rest of the board is held to.

   ⚠ flex-start, not baseline. The name column is three lines and the efficiency block is two; a
   column flex item baselines on its FIRST line, which would hang the efficiency off its caption. */
const Top = styled.div`
  position: relative;
  z-index: 2;
  display: flex;
  align-items: flex-start;
  gap: 30px;
  padding-bottom: 18px;
  margin-bottom: 18px;
  border-bottom: 1px solid ${LINE};

  .pos {
    flex: 0 0 auto;
    font-size: 118px;
    font-weight: 900;
    letter-spacing: -0.03em;
    line-height: 0.86;
    color: var(--c);
    /* no glow on text - see the note in RaceView */
    text-shadow: none;
  }
  .who {
    flex: 1 1 auto;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 6px;
  }
  .name {
    font-size: 58px;
    font-weight: 900;
    letter-spacing: 0.02em;
    line-height: 1;
    margin-bottom: 4px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  /* [2026-09-18, user] TWO DELIBERATE ROWS, the money figures together and then the gap. The
     two money items may break onto two lines on the narrowest canvas, but only BETWEEN them -
     never mid-phrase, which is what read as a layout accident. */
  .row {
    font-size: 32px;
    font-weight: 700;
    letter-spacing: 0.05em;
    line-height: 1.3;
    color: rgba(255, 255, 255, 0.66);
  }
  .money {
    display: flex;
    flex-wrap: wrap;
    span { white-space: nowrap; }
  }
  .pts {
    white-space: nowrap;
    b { color: ${AMBER}; }
  }
  b { color: ${TEXT}; font-weight: 900; }
  em {
    font-style: normal;
    padding: 0 0.45em;
    color: rgba(255, 255, 255, 0.28);
  }

  .effwrap {
    flex: 0 0 auto;
    margin-left: auto;
    display: flex;
    flex-direction: column;
    align-items: flex-end;
  }
  /* [2026-09-18, user] The figure alone did not say WHAT the percentage was of. The caption is
     what makes it readable cold. */
  .efflabel {
    font-size: 20px;
    font-weight: 900;
    letter-spacing: 0.18em;
    line-height: 1;
    margin-bottom: 8px;
    white-space: nowrap;
    color: rgba(255, 255, 255, 0.42);
  }
  .eff {
    font-size: 66px;
    font-weight: 900;
    line-height: 1;
    font-variant-numeric: tabular-nums;
    color: rgba(255, 255, 255, 0.9);
  }
`;

const Lever = styled.div<{ $tone: 'RETENTION' | 'UPSELL' | 'REFUNDS' | 'HOLD' }>`
  position: relative;
  z-index: 2;
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
  gap: 20px;

  /* [2026-09-18, user] A static etching of the car, riding the bottom of the lever region.
     ⚠ ANCHORED TO THIS BLOCK, NOT TO THE CARD, so the rule under it never runs through the car.
     z-index 0 against the text's 1 - Lever sets z-index 2 and so opens its own stacking context.
     ⚠ NO BACKTICKS IN THIS COMMENT. It lives inside a styled-components template literal and a
     backtick here ends the string. */
  .etch {
    position: absolute;
    right: -2%;
    bottom: 0;
    width: 34%;
    z-index: 0;
    opacity: 0.07;
    color: var(--c);
    pointer-events: none;
    svg { width: 100%; height: auto; display: block; }
  }
  .tag, .what, .line, .also, .h { position: relative; z-index: 1; }

  /* The tag and what it means on ONE line - the card is wide enough now, and the rule under
     them keeps the pair reading as a heading for the two sections below. */
  .leverhead {
    display: flex;
    align-items: center;
    gap: 20px;
    padding-bottom: 16px;
    border-bottom: 1px solid ${LINE};
  }
  .tag {
    flex: 0 0 auto;
    font-size: 26px;
    font-weight: 900;
    letter-spacing: 0.2em;
    line-height: 1;
    padding: 8px 14px;
    color: #0B0B0F;
    background: ${(p) => (p.$tone === 'REFUNDS' ? RED
    : p.$tone === 'UPSELL' ? AMBER
      : p.$tone === 'RETENTION' ? GREEN : 'rgba(255,255,255,.75)')};
  }
  .what {
    font-size: 24px;
    font-weight: 700;
    letter-spacing: 0.14em;
    line-height: 1.2;
    color: rgba(255, 255, 255, 0.52);
  }
  /* The professional label. Small, tracked and dim: it names the sentence without competing
     with it - the caption weight every other panel on this board uses. */
  .h {
    font-size: 22px;
    font-weight: 900;
    letter-spacing: 0.2em;
    line-height: 1;
    margin-bottom: 10px;
    color: rgba(255, 255, 255, 0.46);
  }
  /* [2026-09-21] 20px to 38px: clear of the 37px ten-foot floor, which three cards side by side
     could never reach. */
  .line {
    font-size: 38px;
    font-weight: 700;
    line-height: 1.25;
    color: rgba(255, 255, 255, 0.94);
    b { color: var(--c); font-weight: 900; }
  }
  .also {
    font-size: 30px;
    font-weight: 700;
    line-height: 1.3;
    color: rgba(255, 255, 255, 0.62);
    b { color: rgba(255, 255, 255, 0.9); font-weight: 900; }
  }
`;

const Overtake = styled.div`
  position: relative;
  z-index: 2;
  margin-top: 16px;
  padding-top: 14px;
  border-top: 1px solid ${LINE};

  /* The same caption weight as ACTION REQUIRED and ROOT CAUSE above it: the F1 word labels the
     sentence, the sentence says it plainly. */
  .h {
    font-size: 22px;
    font-weight: 900;
    letter-spacing: 0.2em;
    line-height: 1;
    margin-bottom: 10px;
    color: rgba(255, 255, 255, 0.46);
  }
  .say {
    font-size: 34px;
    font-weight: 700;
    letter-spacing: 0.04em;
    line-height: 1.25;
    color: rgba(255, 255, 255, 0.72);
  }
  b { color: ${TEXT}; font-weight: 900; }
`;

/* ── the stack ──────────────────────────────────────────────────────────────── */

/* ⚠ OPAQUE AND ABOVE THE CARD (z-index 3). The arriving card starts half-way under here and
   slides out from beneath it - that is what makes it read as dealt from the stack. */
const Stash = styled.aside`
  flex: 0 0 19%;
  min-width: 0;
  position: relative;
  z-index: 3;
  background: ${INK};
  display: flex;
  flex-direction: column;

  /* The cover carries on past the stack into the page's right margin. The arriving card starts
     more than half its width to the right, and without this its far edge showed in the margin
     beside the stack. 40% of the stack is wider than the 5% page margin at every canvas width. */
  &::before {
    content: '';
    position: absolute;
    top: 0;
    bottom: 0;
    left: 100%;
    width: 40%;
    background: ${INK};
  }

  .cap {
    font-size: 18px;
    font-weight: 900;
    letter-spacing: 0.2em;
    line-height: 1;
    margin-bottom: 14px;
    white-space: nowrap;
    color: rgba(255, 255, 255, 0.46);
  }
  .deck {
    position: relative;
    display: flex;
    flex-direction: column;
    gap: 14px;
  }
  /* The divider between what is left of this visit and the next one. */
  .later {
    display: flex;
    align-items: center;
    gap: 10px;
    margin: 6px 0 0;
    &::after {
      content: '';
      flex: 1 1 auto;
      height: 1px;
      background: ${LINE};
    }
  }
  .pl {
    animation: ${plateIn} 460ms ${EASE} backwards;
    animation-delay: calc(var(--base, 0ms) + var(--k, 0) * ${PLATE_STEP_MS}ms);
  }
  .ghost {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    z-index: 2;
    animation: ${plateOut} 420ms ${EASE_OUT_CARD} forwards;
  }

  @media (prefers-reduced-motion: reduce) {
    .pl { animation: none; }
    .ghost { display: none; }
  }
`;

const Plate = styled.div<{ $lead: boolean }>`
  position: relative;
  display: flex;
  flex-direction: column;
  padding: 16px 18px 20px 26px;
  background: ${(p) => (p.$lead
    ? `linear-gradient(160deg, rgba(255,255,255,.11), rgba(255,255,255,.035)), ${INK}`
    : `linear-gradient(160deg, rgba(255,255,255,.05), rgba(255,255,255,.012)), ${INK}`)};
  border: 1px solid ${(p) => (p.$lead ? 'rgba(255,255,255,.2)' : LINE)};
  clip-path: polygon(16px 0, 100% 0, 100% calc(100% - 16px), calc(100% - 16px) 100%, 0 100%, 0 16px);
  overflow: hidden;

  /* [2026-09-21, user] NO LIVERY BAR DOWN THE LEFT EDGE on a plate - the main card keeps its
     bar, the stack does not. The livery is carried by the P number and the countdown bar. The
     red edge that used to light on the NEXT plate lived on that same edge and went with it. */
  .p {
    font-size: 40px;
    font-weight: 900;
    letter-spacing: -0.02em;
    line-height: 1;
    margin-bottom: 8px;
    color: var(--c);
  }
  .n {
    font-size: 30px;
    font-weight: 900;
    letter-spacing: 0.03em;
    line-height: 1.12;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    color: ${(p) => (p.$lead ? TEXT : 'rgba(255,255,255,.72)')};
  }
  /* The wait, as a bar along the bottom of the NEXT plate. Runs for exactly one card's share. */
  .count {
    position: absolute;
    left: 0;
    right: 0;
    bottom: 0;
    height: 5px;
    background: var(--c);
    transform-origin: left center;
    transform: scaleX(0);
    animation: ${countdown} linear forwards;
    will-change: transform;
  }
`;

const Foot = styled.footer`
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  gap: 16px;
  padding-top: 12px;
  border-top: 2px solid ${LINE};
  font-size: 20px;
  font-weight: 900;
  letter-spacing: 0.16em;
  color: rgba(255, 255, 255, 0.66);

  .t { color: ${AMBER}; }
  b { color: ${TEXT}; }
`;

const Empty = styled.div`
  flex: 1 1 auto;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 30px;
  font-weight: 900;
  letter-spacing: 0.2em;
  color: rgba(255, 255, 255, 0.5);
`;

/* ── copy ───────────────────────────────────────────────────────────────────── */

const money = (v: number) => `$${fmtNum(Math.abs(v), 0)}`;

/**
 * [2026-09-18, user] EVERY CARD ANSWERS TWO QUESTIONS, IN THIS ORDER.
 *
 *   ACTION REQUIRED - what this PM should go and do, with the figure that closes it.
 *   ROOT CAUSE      - why they are at this position, with the figures that put them there.
 *
 * The user's rule: *"headings will be in professional terms and sentences will be in simple
 * english and based on the data the amount will also be there"*. So the labels are the formal
 * ones a debrief sheet carries, and everything under them is a short sentence with a real number
 * in it - no jargon, and no figure that is not on this PM's own row.
 *
 * The "THE ONE THING COSTING YOU THE MOST" caption sits ABOVE them, beside the tag: it explains
 * what the coloured box IS, where these two say what to do about it and why it is there.
 */

/** What the coloured tag beside it means, in plain English. The tag alone is a category, and a
 *  category does not tell a reader why it is on their card. Carries no figures on purpose: the
 *  two sentences below already have those, and repeating them makes the card read twice. */
function leverWhat(lever: CoachCard['lever']): string {
  return lever === 'HOLD'
    ? 'NOTHING IS COSTING YOU RIGHT NOW'
    : 'THE ONE THING COSTING YOU THE MOST';
}

/**
 * What to go and do. The amount is what closes THIS LEVER, never the gap to P1 - telling a P12 to
 * find $45,000 is a number, not an instruction.
 *
 * ⚠ [2026-09-18, user] A REFUND IS NOT ACTIONABLE, so this line NEVER tells anybody to fix one.
 * *"the person cant do anything for the refund, so he will cover it with the retention and upsell
 * only"*. When refunds are the root cause the action routes to whichever of the two ACTIONABLE
 * levers is further behind the field; when the PM is already ahead on both, it names them
 * together rather than inventing a weakness.
 *
 * ⚠ IT STATES THE CAUSE, IT DOES NOT CONSOLE. An earlier draft opened "YOU CANNOT UNDO A
 * REFUND" and the user rejected it outright: *"it is showing sympathy which is wrong as this is
 * for the company dashboard"*. The refund is reported as what it DID - it put them at this rank -
 * and the instruction follows. Same facts, no comfort.
 *
 * "PUT YOU AT THIS RANK", not "COST YOU AT THIS RANK": the second reads as though something was
 * lost WHILE they were at this rank, which is the wrong claim. The refund is the REASON for the
 * position, and the sentence has to say exactly that or the whole card argues something it did
 * not measure.
 */
function actionLine(c: CoachCard): React.ReactNode {
  if (c.lever === 'RETENTION') {
    return <>KEEP <b>{money(c.leverWorth)}</b> MORE OF THE WORK YOU ALREADY HAVE.</>;
  }
  if (c.lever === 'UPSELL') {
    return <>BRING IN <b>{money(c.leverWorth)}</b> MORE UPSELL TO REACH THE FIELD AVERAGE.</>;
  }
  if (c.lever === 'REFUNDS') {
    const best = c.upsellWorth >= c.retentionWorth
      ? { key: 'UPSELL', worth: c.upsellWorth }
      : { key: 'RETENTION', worth: c.retentionWorth };
    if (best.worth > 0) {
      return (
        <>
          REFUNDS PUT YOU AT THIS RANK. WIN <b>{money(c.leverWorth)}</b> BACK THROUGH{' '}
          {best.key === 'UPSELL' ? 'UPSELL' : 'RETENTION'} - YOU ARE{' '}
          <b>{money(best.worth)}</b> BEHIND THE FIELD THERE.
        </>
      );
    }
    return (
      <>
        REFUNDS PUT YOU AT THIS RANK. WIN <b>{money(c.leverWorth)}</b> BACK THROUGH MORE
        RETENTION OR UPSELL.
      </>
    );
  }
  return <>HOLD YOUR PACE. THERE IS NOTHING TO FIX ON THIS CAR.</>;
}

/** Why they are where they are - the same figures, stated as a fact rather than an instruction. */
function causeLine(c: CoachCard): React.ReactNode {
  if (c.lever === 'RETENTION') {
    return (
      <>
        YOU KEEP <b>{c.keptRate.toFixed(0)}%</b> OF WHAT YOU PLACE. THE FIELD KEEPS{' '}
        <b>{c.fieldKeptRate.toFixed(0)}%</b>.
      </>
    );
  }
  if (c.lever === 'UPSELL') {
    return (
      <>
        YOUR UPSELL IS <b>{money(c.totalUpsell)}</b>. THE AVERAGE PM HAS{' '}
        <b>{money(c.fieldAvgUpsell)}</b>.
      </>
    );
  }
  if (c.lever === 'REFUNDS') {
    return (
      <>
        <b>{money(c.refundDisputeLost)}</b> HAS BEEN REFUNDED OR LOST IN DISPUTES
        {c.disputeWon > 0 ? <>, AND ONLY <b>{money(c.disputeWon)}</b> CAME BACK</> : null}.
      </>
    );
  }
  return <>YOU ARE AHEAD OF THE FIELD ON RETENTION, UPSELL AND REFUNDS.</>;
}

/** Named only when it is within 25% of the main lever, so a PM bleeding on two fronts is not
 *  told a half-truth — and a PM with one clear problem is not given two instructions. */
function secondLine(c: CoachCard): React.ReactNode {
  if (!c.secondLever) return null;
  if (c.secondLever === 'RETENTION') {
    return <>ALSO: YOU KEEP ONLY <b>{c.keptRate.toFixed(0)}%</b> OF THE BOOK.</>;
  }
  if (c.secondLever === 'UPSELL') {
    return <>ALSO: <b>{money(c.secondWorth)}</b> OF UPSELL IS ON THE TABLE.</>;
  }
  return <>ALSO: <b>{money(c.refundDisputeLost)}</b> LOST TO REFUNDS.</>;
}

/**
 * The overtake — the car directly ahead, not the leader, because that is the one they can
 * actually reach this month.
 *
 * [2026-09-21, user] EXACT, AND IN PLAIN WORDS. It used to say "ONE GOOD WEEK", "THAT IS ONE
 * CLIENT" and "BUILD IT, DO NOT CHASE IT" - judgements no figure on the card backs up. It now
 * says how much, of what, to pass whom.
 *
 * ⚠ THE DOLLAR GAP IS THE WHOLE STORY, which is why no efficiency figure appears here. Effective
 * inventory is the SAME for every PM in a month (services/pmSingaporeLeaderboard.ts), so
 * efficiency is total in bank over one shared number and the rank order IS total-in-bank order.
 * Total in bank is retention + upsell - refunds + disputes won, so a dollar of either counts the
 * same - hence "RETENTION OR UPSELL".
 *
 * ⚠ ONE DOLLAR PAST THE GAP. Matching the gap only draws level, and a tie is settled by
 * alphabetical name - which may or may not go their way. The first whole dollar above the gap
 * always passes. The gap can be 0 (level); below 0 cannot happen under this ranking, and is
 * clamped rather than trusted.
 */
function overtakeHead(c: CoachCard): string {
  return c.aheadPosition === 3
    ? 'OVERTAKE FOR P3 · PODIUM'
    : `OVERTAKE FOR P${c.aheadPosition}`;
}

function overtakeLine(c: CoachCard): React.ReactNode {
  const pass = Math.floor(Math.max(0, c.gapToAhead)) + 1;
  const who = c.aheadName.split(' ')[0].toUpperCase();
  return <>BANK <b>{money(pass)}</b> MORE IN RETENTION OR UPSELL TO PASS <b>{who}</b>.</>;
}

/**
 * [2026-09-21, user] WHOEVER HOLDS THIS RANK RIGHT NOW. A visit is three RANKS, not three people:
 * a card that comes up later in the visit shows the live P5, so a PM who has climbed onto the
 * podium since the visit began never comes up here - the debrief is ranks 4 to last, always.
 * Falls back to the arrival snapshot only if the rank itself has gone (the field shrank).
 */
function holderOf(live: CoachCard[], slot: CoachCard): CoachCard {
  return live.find((c) => c.position === slot.position) || slot;
}

/** First name on one line, the rest on the next - a stack plate is too narrow for both. */
function splitName(name: string): [string, string] {
  const parts = name.trim().toUpperCase().split(/\s+/);
  return [parts[0] || '', parts.slice(1).join(' ')];
}

const lnStyle = (i: number) => ({ '--i': i } as React.CSSProperties);

/* ── the card ───────────────────────────────────────────────────────────────── */

interface CardProps {
  c: CoachCard;
  color: string;
  slot: number;
  entry: 'first' | 'swap';
  leaving: boolean;
}

const DebriefCard: React.FC<CardProps> = ({ c, color, slot, entry, leaving }) => {
  const also = secondLine(c);
  return (
    <Card $entry={entry} $leaving={leaving} style={{ '--c': color } as React.CSSProperties}>
      <i className="rake" />

      {/* [2026-09-18, user] What this PM HAS — retained and upsells — and then how far off P1
          they are in points. */}
      <Top>
        <span className="pos ln" style={lnStyle(0)}>P{c.position}</span>
        <div className="who">
          <div className="name ln" style={lnStyle(0)}>{c.pmName.toUpperCase()}</div>
          <div className="row money ln" style={lnStyle(1)}>
            <span>RETAINED: <b>{money(c.placedInBank)}</b></span>
            <em>|</em>
            <span>UPSELLS: <b>{money(c.totalUpsell)}</b></span>
          </div>
          <div className="row pts ln" style={lnStyle(2)}>
            <b>{c.gapPoints.toFixed(1)}</b> POINTS FROM P1
          </div>
        </div>
        <span className="effwrap ln" style={lnStyle(1)}>
          <span className="efflabel">EFFICIENCY %</span>
          <span className="eff">{fmtPctValue(c.efficiencyPct)}</span>
        </span>
      </Top>

      <Lever $tone={c.lever}>
        {/* ⚠ SVG gradient ids are global: two cards are on screen during a swap, so the suffix
            is the card's slot in the visit, which is unique among them. */}
        <div className="etch"><SideCarSvg idSuffix={`dbf${slot}`} /></div>
        <div className="leverhead ln" style={lnStyle(3)}>
          <span className="tag">{c.lever}</span>
          <span className="what">{leverWhat(c.lever)}</span>
        </div>

        <div className="block ln" style={lnStyle(4)}>
          <div className="h">ACTION REQUIRED</div>
          <div className="line">{actionLine(c)}</div>
        </div>

        <div className="block ln" style={lnStyle(5)}>
          <div className="h">ROOT CAUSE</div>
          <div className="line">{causeLine(c)}</div>
        </div>

        {also && <div className="also ln" style={lnStyle(6)}>{also}</div>}
      </Lever>

      <Overtake className="ln" style={lnStyle(7)}>
        <div className="h">{overtakeHead(c)}</div>
        <div className="say">{overtakeLine(c)}</div>
      </Overtake>
    </Card>
  );
};

/* ── the view ───────────────────────────────────────────────────────────────── */

interface Props {
  data: LeaderData;
  entryToken: number;
  /** This arrival's group and where to open it. Owned by PMSingaporePage — see the header. */
  visit: DebriefVisit;
  /** Told which card is up, so an announcement can hand the board back to the same one. */
  onCard: (index: number) => void;
}

const PitDebriefView: React.FC<Props> = ({ data, entryToken, visit, onCard }) => {
  /* The visit's RANK SLOTS (P4-P5-P6, say), as they stood at arrival. Who fills each one is read
     live - see holderOf. */
  const cards = visit.cards;
  const n = cards.length;
  /* Read from the timer, which must not re-arm on every poll. Assigned on every render. */
  const liveRef = useRef(data.coaching);
  liveRef.current = data.coaching;
  const [idx, setIdx] = useState(() => Math.min(visit.start, Math.max(0, n - 1)));
  /** Who each card showed, taken the moment it came up and held while it is on screen. */
  const [pinned, setPinned] = useState<Record<number, CoachCard>>(() => {
    const first = Math.min(visit.start, Math.max(0, n - 1));
    return n ? { [first]: holderOf(data.coaching, cards[first]) } : {};
  });
  /** The card sliding away during a swap, for the moment both are on screen. */
  const [prev, setPrev] = useState<number | null>(null);

  useEffect(() => { onCard(idx); }, [idx, onCard]);

  /* One card's share, then the next. ⚠ THE LAST CARD HOLDS - no step past it. The circuit moves
     the board on at the end of the dwell, and a step fired at that same instant would start a
     swap under the incoming page transition. */
  useEffect(() => {
    if (idx >= n - 1) return undefined;
    const t = window.setTimeout(() => {
      const next = idx + 1;
      setPinned((p) => ({ ...p, [next]: holderOf(liveRef.current, cards[next]) }));
      setPrev(idx);
      setIdx(next);
    }, visit.perCardMs);
    return () => window.clearTimeout(t);
  }, [idx, n, cards, visit.perCardMs]);

  useEffect(() => {
    if (prev === null) return undefined;
    const t = window.setTimeout(() => setPrev(null), OUT_MS + 60);
    return () => window.clearTimeout(t);
  }, [prev]);

  /* The card ON SCREEN keeps its person until its share is up; the FIGURES are read live, by
     name, so a poll that moves a number updates the card in place. If that PM climbs onto the
     podium meanwhile they stay up with the figures they came up with - their card is finishing,
     and pulling it mid-read would look broken. Nothing after it shows them again. */
  const live = data.coaching;
  const byName = new Map(live.map((c) => [c.pmName, c]));
  const current = (c: CoachCard) => byName.get(c.pmName) || c;
  const onScreen = (i: number) => current(pinned[i] || holderOf(live, cards[i]));
  const colorFor = (position: number) =>
    data.runners.find((r) => r.rank === position)?.color || '#8A8A96';
  const leader = data.runners[0] || null;
  const lastRank = live.length ? live[live.length - 1].position : 0;

  /* The next three names in rank order: the rest of this visit, then the next visit's group. The
     parent's cursor already points one past this group, so the next visit is group + 1. Every
     name here is whoever holds that rank NOW, so the stack always names the card that will
     actually come up. */
  const groupsNow = Math.max(1, Math.ceil(live.length / DEBRIEF_GROUP));
  const nextGroup = visit.group + 1 < groupsNow ? visit.group + 1 : 0;   // the parent's rule
  const queue = [
    ...cards.slice(idx + 1).map((slot) => ({ c: holderOf(live, slot), later: false })),
    ...live
      .slice(nextGroup * DEBRIEF_GROUP, (nextGroup + 1) * DEBRIEF_GROUP)
      .map((c) => ({ c, later: true })),
  ].slice(0, DEBRIEF_GROUP);

  /* The plates deal in with the first card, and move up a slot on every swap after it. */
  const opening = prev === null && idx === visit.start;
  const deckBase = `${opening ? FIRST_DELAY_MS : PLATE_LAG_MS}ms`;
  const ghost = prev !== null ? onScreen(idx) : null;
  const shown = prev !== null ? [prev, idx] : [idx];

  return (
    <View key={entryToken}>
      <Head>
        <div className="mark">
          <span className="m1">PIT</span>
          <span className="m2">DEBRIEF</span>
          <span className="sub">
            WHAT IT TAKES TO REACH P1
            {live.length ? ` · RANKS 4-${lastRank}` : ''} · STARTING {data.startMonth}
          </span>
        </div>
        {visit.groups > 1 && <div className="page">PAGE {visit.group + 1} OF {visit.groups}</div>}
      </Head>

      {n ? (
        <Bay>
          <Main>
            {shown.map((i) => {
              const c = onScreen(i);
              return (
                <DebriefCard
                  key={i}
                  c={c}
                  color={colorFor(c.position)}
                  slot={i}
                  entry={i === visit.start ? 'first' : 'swap'}
                  leaving={i === prev}
                />
              );
            })}
          </Main>

          <Stash>
            <div className="cap">{queue.length && queue[0].later ? 'NEXT VISIT' : 'NEXT UP'}</div>
            {/* Re-keyed on every swap so the plates replay their rise into the new slots. */}
            <div className="deck" key={idx} style={{ '--base': deckBase } as React.CSSProperties}>
              {ghost && (
                <Plate className="ghost" $lead style={{ '--c': colorFor(ghost.position) } as React.CSSProperties}>
                  <span className="p">P{ghost.position}</span>
                  <span className="n">{splitName(ghost.pmName)[0]}</span>
                  <span className="n">{splitName(ghost.pmName)[1] || ' '}</span>
                </Plate>
              )}
              {queue.map((q, k) => {
                const lead = k === 0 && !q.later;
                const [first, rest] = splitName(q.c.pmName);
                return (
                  <React.Fragment key={`${q.c.pmName}${q.later ? '-next' : ''}`}>
                    {q.later && k > 0 && !queue[k - 1].later && (
                      <div className="cap later pl" style={{ '--k': k } as React.CSSProperties}>NEXT VISIT</div>
                    )}
                    <Plate
                      className="pl"
                      $lead={lead}
                      style={{ '--c': colorFor(q.c.position), '--k': k } as React.CSSProperties}
                    >
                      <span className="p">P{q.c.position}</span>
                      <span className="n">{first}</span>
                      <span className="n">{rest || ' '}</span>
                      {lead && (
                        <i className="count" style={{ animationDuration: `${visit.perCardMs}ms` }} />
                      )}
                    </Plate>
                  </React.Fragment>
                );
              })}
            </div>
          </Stash>
        </Bay>
      ) : (
        <Empty>EVERY PM IS ON THE PODIUM</Empty>
      )}

      <Foot>
        <span className="t">TARGET</span>
        {leader ? (
          <span>
            P1 <b>{leader.name.toUpperCase()}</b> · <b>${fmtNum(leader.raw.totalInBank, 0)}</b> IN
            BANK · <b>{leader.metric}</b>
          </span>
        ) : (
          <span>NO RUNNER</span>
        )}
      </Foot>
    </View>
  );
};

export default React.memo(PitDebriefView);
